import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, Minus, Send, MessageCircle, Sparkles, CalendarPlus, Wallet, Users, Search, Paperclip, CornerDownRight, Bot } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtimeEvents } from "@/hooks/useRealtimeEvents";
import { ChatMessage } from "@/components/chat/ChatMessage";
import { VoiceRecorder, blobToBase64 } from "@/components/chat/VoiceRecorder";
import { Avatar } from "@/components/Avatar";
import { EmojiButton, StickerButton } from "@/components/chat/EmojiPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_OPEN = 3; // max number of open chat windows side by side

/**
 * AdminChatDock — Facebook-style multi-chat dock for admin (bottom-right strip).
 * Each chat = one user. Auto-opens a window when a new user message arrives.
 * Windows can be minimized (collapsed to a small bar) or closed.
 */
export const AdminChatDock = () => {
  const { user, authHeader } = useAuth();
  const [windows, setWindows] = useState([]);
  const [chatCache, setChatCache] = useState({});
  const [drafts, setDrafts] = useState({});
  const [replyingTo, setReplyingTo] = useState({});
  const [userTyping, setUserTyping] = useState({}); // {uid: bool}
  const typingTimersRef = useRef({});
  const lastTypingPingRef = useRef({});
  const [sending, setSending] = useState({});
  // Connected/disconnected users panel
  const [usersList, setUsersList] = useState([]);
  const [showUsers, setShowUsers] = useState(false);
  const [aiPaused, setAiPaused] = useState({}); // {uid: bool}
  const toggleAi = async (uid) => {
    const next = !aiPaused[uid];
    try {
      await axios.post(`${API}/admin/client-ai/pause/${uid}?paused=${next}`, {}, { headers: authHeader() });
      setAiPaused((p) => ({ ...p, [uid]: next }));
    } catch { /* noop */ }
  };
  const [filter, setFilter] = useState("");
  const [filterOnline, setFilterOnline] = useState("all"); // all | online | offline
  const scrollRefs = useRef({});

  const fetchUsersList = async () => {
    try {
      const r = await axios.get(`${API}/admin/chat-users`, { headers: authHeader() });
      setUsersList(r.data || []);
    } catch { /* ignore */ }
  };

  const fetchUserMessages = async (uid) => {
    try {
      const r = await axios.get(`${API}/admin/users/${uid}/messages`, { headers: authHeader() });
      const msgs = r.data?.messages || r.data || [];
      setChatCache((c) => ({ ...c, [uid]: msgs }));
      // refresh users list so unread badge updates
      fetchUsersList();
    } catch { /* ignore */ }
  };

  const openChat = async (info) => {
    if (!info?.user_id) return;
    axios.get(`${API}/admin/client-ai/pause/${info.user_id}`, { headers: authHeader() })
      .then((r) => setAiPaused((p) => ({ ...p, [info.user_id]: !!r.data?.ai_paused }))).catch(() => {});
    setWindows((arr) => {
      // already open? Make sure it's not minimized
      const exists = arr.find((w) => w.user_id === info.user_id);
      if (exists) {
        return arr.map((w) => (w.user_id === info.user_id ? { ...w, minimized: false } : w));
      }
      // limit number of side-by-side windows
      const next = [{ ...info, minimized: false }, ...arr];
      return next.slice(0, MAX_OPEN);
    });
    fetchUserMessages(info.user_id);
  };

  const closeChat = (uid) => {
    setWindows((arr) => arr.filter((w) => w.user_id !== uid));
  };

  const toggleMin = (uid) => {
    setWindows((arr) => arr.map((w) => (w.user_id === uid ? { ...w, minimized: !w.minimized } : w)));
  };

  useRealtimeEvents({
    message: async (data) => {
      // Always refresh users list on any new message
      fetchUsersList();
      if (!data) return;
      if (data.from_role !== "user") {
        // Even admin-side echoes refresh open windows so we see delivery state
        if (data.user_id && chatCache[data.user_id]) fetchUserMessages(data.user_id);
        return;
      }
      const exists = windows.find((w) => w.user_id === data.user_id);
      if (exists) {
        fetchUserMessages(data.user_id);
        return;
      }
      try {
        const r = await axios.get(`${API}/admin/users/${data.user_id}`, { headers: authHeader() });
        const u = r.data || {};
        openChat({ user_id: data.user_id, user_name: u.name || u.username, visible_id: u.visible_id, has_profile_pic: !!u.profile_pic_b64 });
      } catch {
        openChat({ user_id: data.user_id, user_name: "Cliente", visible_id: "" });
      }
    },
    message_read: (data) => {
      if (data?.user_id && chatCache[data.user_id]) fetchUserMessages(data.user_id);
    },
    presence: () => fetchUsersList(),
    download_request: () => fetchUsersList(),
    message_reaction: (data) => { if (data?.user_id && chatCache[data.user_id]) fetchUserMessages(data.user_id); },
    message_edit: (data) => { if (data?.user_id && chatCache[data.user_id]) fetchUserMessages(data.user_id); },
    message_delete: (data) => { if (data?.user_id && chatCache[data.user_id]) fetchUserMessages(data.user_id); },
    profile_pic_changed: (data) => {
      try { window.__bumpAvatarCache?.(data?.user_id); } catch { /* noop */ }
      fetchUsersList();
    },
    typing: (data) => {
      if (data?.user_id && data.from_role === "user") {
        setUserTyping((u) => ({ ...u, [data.user_id]: true }));
        if (typingTimersRef.current[data.user_id]) clearTimeout(typingTimersRef.current[data.user_id]);
        typingTimersRef.current[data.user_id] = setTimeout(
          () => setUserTyping((u) => ({ ...u, [data.user_id]: false })), 3000);
      }
    },
  });

  const pingTyping = (uid) => {
    const now = Date.now();
    if (now - (lastTypingPingRef.current[uid] || 0) < 2500) return;
    lastTypingPingRef.current[uid] = now;
    axios.post(`${API}/admin/users/${uid}/typing`, {}, { headers: authHeader() }).catch(() => null);
  };

  // Initial fetch + every 30s refresh of users list
  useEffect(() => {
    if (!user || user.role !== "admin") return undefined;
    fetchUsersList();
    const t = setInterval(fetchUsersList, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [user]);

  // Listen for explicit "open chat with user" requests from elsewhere
  useEffect(() => {
    const onOpen = (e) => openChat(e.detail || {});
    window.addEventListener("admin:openChat", onOpen);
    return () => window.removeEventListener("admin:openChat", onOpen);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    // scroll all open windows to bottom on cache change
    Object.entries(scrollRefs.current).forEach(([uid, el]) => {
      if (el && chatCache[uid]) el.scrollTop = el.scrollHeight;
    });
  }, [chatCache]);

  if (!user || user.role !== "admin") return null;

  const send = async (uid) => {
    const body = (drafts[uid] || "").trim();
    if (!body || sending[uid]) return;
    setSending((s) => ({ ...s, [uid]: true }));
    try {
      await axios.post(`${API}/admin/users/${uid}/messages`, {
        body, kind: "text", reply_to: replyingTo[uid]?.id || null,
      }, { headers: authHeader() });
      setDrafts((d) => ({ ...d, [uid]: "" }));
      setReplyingTo((r) => ({ ...r, [uid]: null }));
      await fetchUserMessages(uid);
    } catch { /* ignore */ }
    finally { setSending((s) => ({ ...s, [uid]: false })); }
  };

  const sendMedia = async (uid, { blob, mimeType, durationSec, kind }) => {
    if (!blob) return;
    setSending((s) => ({ ...s, [uid]: true }));
    try {
      const b64 = await blobToBase64(blob);
      await axios.post(`${API}/admin/users/${uid}/messages`, {
        body: "", kind, media_b64: b64, media_mime: mimeType, duration_sec: durationSec,
        reply_to: replyingTo[uid]?.id || null,
      }, { headers: authHeader() });
      setReplyingTo((r) => ({ ...r, [uid]: null }));
      await fetchUserMessages(uid);
    } catch (e) { alert(e?.response?.data?.detail || "Error enviando media"); }
    finally { setSending((s) => ({ ...s, [uid]: false })); }
  };

  const sendSticker = async (uid, stickerId) => {
    setSending((s) => ({ ...s, [uid]: true }));
    try {
      await axios.post(`${API}/admin/users/${uid}/messages`, {
        body: "", kind: "sticker", sticker: stickerId, reply_to: replyingTo[uid]?.id || null,
      }, { headers: authHeader() });
      setReplyingTo((r) => ({ ...r, [uid]: null }));
      await fetchUserMessages(uid);
    } catch { /* ignore */ }
    finally { setSending((s) => ({ ...s, [uid]: false })); }
  };

  const filteredUsers = usersList.filter((u) => {
    if (filterOnline === "online" && !u.online) return false;
    if (filterOnline === "offline" && u.online) return false;
    if (!filter.trim()) return true;
    const f = filter.toLowerCase();
    return (u.name || "").toLowerCase().includes(f)
        || (u.username || "").toLowerCase().includes(f)
        || (u.visible_id || "").toLowerCase().includes(f);
  });

  return (
    <div className="fixed bottom-0 right-4 z-[75] flex items-end gap-2 pointer-events-none" data-testid="admin-chat-dock">
      {/* Users panel toggle (always visible to admin) */}
      <div className="pointer-events-auto">
        {showUsers ? (
          <div className="w-72 h-[24rem] bg-gradient-to-b from-[#1a1a1a] to-[#0a0a0a] border-t-2 border-x-2 border-[#d4af37]/40 rounded-t-2xl shadow-2xl flex flex-col" data-testid="admin-chat-users-panel">
            <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-white/10 bg-gradient-to-r from-[#d4af37]/15 to-amber-500/15">
              <span className="text-xs font-semibold text-white flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5 text-[#d4af37]" /> Clientes ({usersList.filter((u) => u.online).length} online)
              </span>
              <button onClick={() => setShowUsers(false)} className="h-6 w-6 rounded hover:bg-white/10 flex items-center justify-center text-neutral-300">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="px-2 py-1.5 border-b border-white/5 flex items-center gap-1">
              <Search className="w-3 h-3 text-neutral-500" />
              <input
                data-testid="admin-chat-search"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Buscar cliente..."
                className="flex-1 bg-transparent text-[11px] text-white placeholder:text-neutral-500 outline-none"
              />
            </div>
            <div className="px-2 py-1 flex gap-1 border-b border-white/5">
              {["all", "online", "offline"].map((f) => (
                <button
                  key={f}
                  data-testid={`admin-chat-filter-${f}`}
                  onClick={() => setFilterOnline(f)}
                  className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${filterOnline === f ? "border-[#d4af37] text-[#d4af37] bg-[#d4af37]/10" : "border-white/10 text-neutral-400"}`}
                >{f === "all" ? "Todos" : f === "online" ? "🟢 Online" : "⚫ Offline"}</button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto">
              {filteredUsers.length === 0 ? (
                <p className="text-[11px] text-neutral-500 italic text-center py-6">Sin clientes</p>
              ) : filteredUsers.map((u) => (
                <button
                  key={u.id}
                  data-testid={`admin-chat-user-${u.id}`}
                  onClick={() => openChat({ user_id: u.id, user_name: u.name || u.username, visible_id: u.visible_id, has_profile_pic: u.has_profile_pic })}
                  className="w-full text-left px-2.5 py-1.5 hover:bg-white/5 border-b border-white/5 flex items-center gap-2"
                >
                  <div className="relative">
                    <Avatar userId={u.id} name={u.name || u.username} hasPicHint={u.has_profile_pic} authHeader={authHeader} size={28} />
                    <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border border-black ${u.online ? "bg-emerald-500" : "bg-neutral-600"}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-white font-semibold truncate flex items-center gap-1">
                      {u.name || u.username}
                      {u.unread_count > 0 && (
                        <span className="text-[9px] bg-red-500 text-white rounded-full px-1.5 py-0.5">{u.unread_count}</span>
                      )}
                    </div>
                    <div className="text-[10px] text-neutral-500 truncate">{u.last_message || u.visible_id}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <button
            data-testid="admin-chat-users-toggle"
            onClick={() => { setShowUsers(true); fetchUsersList(); }}
            className="w-44 bg-gradient-to-r from-[#1a1a1a] to-[#2a2a2a] border-t-2 border-x-2 border-[#d4af37]/40 rounded-t-2xl px-3 py-2 flex items-center gap-2 text-white hover:bg-[#222]"
          >
            <Users className="w-3.5 h-3.5 text-[#d4af37]" />
            <span className="text-xs font-semibold">Clientes</span>
            <span className="ml-auto w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          </button>
        )}
      </div>

      {windows.map((w) => {
        const msgs = chatCache[w.user_id] || [];
        if (w.minimized) {
          return (
            <button
              key={w.user_id}
              data-testid={`admin-chat-min-${w.user_id}`}
              onClick={() => toggleMin(w.user_id)}
              className="pointer-events-auto w-56 bg-gradient-to-r from-[#1a1a1a] to-[#2a2a2a] border-t-2 border-x-2 border-[#d4af37]/40 rounded-t-2xl px-3 py-2 flex items-center justify-between hover:bg-[#222]"
            >
              <span className="text-xs font-semibold text-white truncate flex items-center gap-1.5">
                <MessageCircle className="w-3.5 h-3.5 text-[#d4af37] flex-shrink-0" />
                {w.user_name}
              </span>
              <span className="text-[10px] text-neutral-400 ml-1">{msgs.length}</span>
            </button>
          );
        }
        return (
          <div
            key={w.user_id}
            data-testid={`admin-chat-window-${w.user_id}`}
            className="pointer-events-auto w-[20rem] h-[24rem] bg-gradient-to-b from-[#1a1a1a] to-[#0a0a0a] border-t-2 border-x-2 border-[#d4af37]/40 rounded-t-2xl shadow-2xl flex flex-col"
          >
            <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-white/10 bg-gradient-to-r from-[#d4af37]/15 to-amber-500/15">
              <div className="flex items-center gap-1.5 min-w-0">
                <button
                  data-testid={`admin-chat-header-${w.user_id}`}
                  onClick={() => window.dispatchEvent(new CustomEvent("admin:openDossier", { detail: { user_id: w.user_id } }))}
                  className="flex items-center gap-1.5 min-w-0 hover:bg-white/10 rounded px-1 -ml-1 py-0.5"
                  title="Ver expediente completo"
                >
                  <Avatar userId={w.user_id} name={w.user_name} hasPicHint={w.has_profile_pic} authHeader={authHeader} size={24} className="flex-shrink-0" />
                  <div className="min-w-0 text-left">
                    <div className="text-white text-xs font-semibold truncate">{w.user_name}</div>
                    <div className="text-[10px] text-neutral-400 font-mono truncate">{w.visible_id}</div>
                  </div>
                </button>
              </div>
              <div className="flex items-center gap-0.5 flex-shrink-0">
                <button
                  data-testid={`admin-chat-ai-toggle-${w.user_id}`}
                  onClick={() => toggleAi(w.user_id)}
                  title={aiPaused[w.user_id] ? "IA pausada para este cliente · clic para reactivar" : "IA respondiendo por ti · clic para pausar y tomar el control"}
                  className={`h-6 px-1.5 rounded text-[9px] font-bold uppercase tracking-widest flex items-center gap-1 ${aiPaused[w.user_id] ? "bg-neutral-700 text-neutral-300" : "bg-[#d4af37]/20 text-[#d4af37]"}`}
                >
                  <Bot className="w-3 h-3" /> {aiPaused[w.user_id] ? "IA off" : "IA on"}
                </button>
                <button
                  data-testid={`admin-chat-minimize-${w.user_id}`}
                  onClick={() => toggleMin(w.user_id)}
                  className="h-6 w-6 rounded hover:bg-white/10 flex items-center justify-center text-neutral-300"
                ><Minus className="w-3.5 h-3.5" /></button>
                <button
                  data-testid={`admin-chat-close-${w.user_id}`}
                  onClick={() => closeChat(w.user_id)}
                  className="h-6 w-6 rounded hover:bg-white/10 flex items-center justify-center text-neutral-300"
                ><X className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            <div ref={(el) => { scrollRefs.current[w.user_id] = el; }} className="flex-1 overflow-y-auto px-2.5 py-2 space-y-1.5">
              {msgs.length === 0 ? (
                <p className="text-[11px] text-neutral-500 italic text-center py-4">Sin mensajes</p>
              ) : msgs.map((m) => (
                <ChatMessage
                  key={m.id}
                  msg={m}
                  isOwn={m.from_role === "admin"}
                  authHeader={authHeader}
                  mediaEndpointPrefix="/admin/messages"
                  viewerRole="admin"
                  peerInfo={{ id: w.user_id, name: w.user_name, has_profile_pic: w.has_profile_pic }}
                  selfInfo={{ id: user.id, name: user.name || user.username, has_profile_pic: false }}
                  allMessages={msgs}
                  onReply={(parent) => setReplyingTo((r) => ({ ...r, [w.user_id]: parent }))}
                />
              ))}
            </div>
            {/* Quick action shortcuts */}
            <div className="border-t border-white/10 px-1.5 py-1 flex gap-1 bg-black/40">
              <button
                data-testid={`admin-quick-grant-${w.user_id}`}
                onClick={() => window.dispatchEvent(new CustomEvent("admin:quickGrant", { detail: { user_id: w.user_id, user_name: w.user_name, visible_id: w.visible_id } }))}
                className="flex-1 text-[10px] py-1 px-1 rounded border border-purple-500/30 text-purple-200 hover:bg-purple-500/10 flex items-center justify-center gap-1"
                title="Habilitar lectura online para este cliente"
              ><Sparkles className="w-3 h-3" /> Lectura</button>
              <button
                data-testid={`admin-quick-appt-${w.user_id}`}
                onClick={() => window.dispatchEvent(new CustomEvent("admin:quickAppt", { detail: { user_id: w.user_id, user_name: w.user_name, visible_id: w.visible_id } }))}
                className="flex-1 text-[10px] py-1 px-1 rounded border border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/10 flex items-center justify-center gap-1"
                title="Registrar cita"
              ><CalendarPlus className="w-3 h-3" /> Cita</button>
              <button
                data-testid={`admin-quick-pay-${w.user_id}`}
                onClick={() => window.dispatchEvent(new CustomEvent("admin:quickPay", { detail: { user_id: w.user_id, user_name: w.user_name, visible_id: w.visible_id } }))}
                className="flex-1 text-[10px] py-1 px-1 rounded border border-amber-500/30 text-amber-200 hover:bg-amber-500/10 flex items-center justify-center gap-1"
                title="Registrar pago/trabajo"
              ><Wallet className="w-3 h-3" /> Pago</button>
            </div>
            {/* Reply preview */}
            {replyingTo[w.user_id] && (
              <div data-testid={`admin-reply-preview-${w.user_id}`} className="px-2 py-1 bg-[#d4af37]/10 border-t border-[#d4af37]/30 text-[10px] flex items-center gap-1.5">
                <CornerDownRight className="w-3 h-3 text-[#d4af37]" />
                <span className="text-neutral-400 italic flex-1 truncate">Resp: {(replyingTo[w.user_id].body || "(media)").slice(0, 50)}</span>
                <button onClick={() => setReplyingTo((r) => ({ ...r, [w.user_id]: null }))} className="text-neutral-400 hover:text-white"><X className="w-3 h-3" /></button>
              </div>
            )}
            <div className="border-t border-white/10 p-1.5 flex gap-1 items-center">
              <input
                type="file"
                accept="image/*"
                data-testid={`admin-chat-image-input-${w.user_id}`}
                className="hidden"
                ref={(el) => { if (el) window[`__adminChatImage_${w.user_id}`] = el; }}
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (!f) return;
                  if (f.size > 6 * 1024 * 1024) return alert("Imagen muy grande (max 6MB)");
                  await sendMedia(w.user_id, { blob: f, mimeType: f.type, kind: "image" });
                }}
              />
              <button
                type="button"
                data-testid={`admin-chat-image-btn-${w.user_id}`}
                onClick={() => window[`__adminChatImage_${w.user_id}`]?.click()}
                className="h-7 w-7 rounded-full bg-black/40 border border-white/10 hover:border-[#d4af37]/50 text-neutral-300 hover:text-[#d4af37] flex items-center justify-center"
                title="Imagen"
              ><Paperclip className="w-3.5 h-3.5" /></button>
              <EmojiButton onSelect={(e) => setDrafts((d) => ({ ...d, [w.user_id]: (d[w.user_id] || "") + e }))} />
              <StickerButton onSelect={(id) => sendSticker(w.user_id, id)} />
              <VoiceRecorder onComplete={(p) => sendMedia(w.user_id, { ...p, kind: "voice" })} disabled={sending[w.user_id]} />
              <input
                data-testid={`admin-chat-input-${w.user_id}`}
                value={drafts[w.user_id] || ""}
                onChange={(e) => { setDrafts((d) => ({ ...d, [w.user_id]: e.target.value })); pingTyping(w.user_id); }}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(w.user_id); } }}
                placeholder="Responder..."
                className="flex-1 bg-black/50 border border-white/10 rounded-full px-2.5 py-1.5 text-[11px] text-white placeholder:text-neutral-500 focus:outline-none focus:border-[#d4af37]/50"
              />
              <button
                data-testid={`admin-chat-send-${w.user_id}`}
                onClick={() => send(w.user_id)}
                disabled={sending[w.user_id] || !(drafts[w.user_id] || "").trim()}
                className="h-7 w-7 rounded-full bg-gradient-to-br from-[#d4af37] to-amber-600 text-black flex items-center justify-center disabled:opacity-40"
              ><Send className="w-3 h-3" /></button>
            </div>
          </div>
        );
      })}
    </div>
  );
};
